import { z } from "zod";
import { memoryApplicabilitySchema, memoryAuthoringVerificationSchema, memoryEntrySchema, memoryKindSchema, memoryLifecycleIdempotencyKeySchema, memoryRelationSchema, memoryScopeTypeSchema, memoryStatusSchema, memoryTypeSchema, memoryVersionIdSchema, memoryVersionSchema, } from "./memories.js";
const identifierSchema = z.string().trim().min(1).max(2_048);
const timestampSchema = z.number().int().nonnegative();
const expectedVersionSchema = z.number().int().positive();
const reasonSchema = z.string().trim().min(1).max(500);
const mutationControl = {
    confirmation: z.literal(true),
    idempotencyKey: memoryLifecycleIdempotencyKeySchema,
};
export const memoryLifecycleActionSchema = z.enum([
    "create",
    "approve",
    "correct",
    "retract",
    "do_not_reuse",
    "restore",
    "edit_scope",
    "forget",
]);
export const memoryInteractiveConfirmationActionSchema = z.enum([
    "save",
    "correct",
    "do_not_reuse",
    "forget",
]);
export const memoryInteractiveConfirmationArgsSchema = z.object({
    kind: z.literal("memory_confirmation"),
    approvalId: identifierSchema,
    issuedRunId: identifierSchema,
    action: memoryInteractiveConfirmationActionSchema,
    preview: z.unknown(),
    command: z.unknown(),
    payload: z.unknown(),
    expiresAt: timestampSchema,
}).strict();
export const memoryLifecycleReceiptSchema = z.object({
    id: identifierSchema,
    action: memoryLifecycleActionSchema,
    idempotencyKey: memoryLifecycleIdempotencyKeySchema,
    actorId: identifierSchema,
    requestHash: z.string().regex(/^[a-f0-9]{64}$/u),
    targetId: identifierSchema.optional(),
    createdAt: timestampSchema,
    replayed: z.boolean(),
}).strict();
export const patchMemoryInputSchema = z.object({
    ...mutationControl,
    status: z.literal("active"),
    expectedVersion: expectedVersionSchema,
    expectedStatus: z.enum(["proposed", "pending", "pending_approval"]),
}).strict();
export const correctMemoryInputSchema = z.object({
    ...mutationControl,
    expectedVersion: expectedVersionSchema,
    expectedStatus: z.literal("active"),
    kind: memoryKindSchema,
    summary: z.string().trim().min(1).max(500),
    value: z.record(z.string(), z.unknown()).default({}),
    memoryType: memoryTypeSchema,
    confidence: z.number().min(0).max(1).nullable().optional(),
    applicability: memoryApplicabilitySchema.default({}),
    validFrom: timestampSchema.optional(),
    validTo: timestampSchema.optional(),
    observedAt: timestampSchema.optional(),
    verification: memoryAuthoringVerificationSchema.default({ status: "unverified" }),
    sourceSessionId: identifierSchema.optional(),
    sourceRunId: identifierSchema.optional(),
}).strict().superRefine((input, context) => {
    if (input.sourceRunId && !input.sourceSessionId) {
        context.addIssue({
            code: "custom",
            path: ["sourceRunId"],
            message: "sourceRunId requires sourceSessionId",
        });
    }
    if (input.validFrom !== undefined && input.validTo !== undefined &&
        input.validTo <= input.validFrom) {
        context.addIssue({
            code: "custom",
            path: ["validTo"],
            message: "validTo must be greater than validFrom",
        });
    }
});
const retractableStatusSchema = z.enum([
    "proposed",
    "pending",
    "pending_approval",
    "active",
    "disputed",
]);
export const retractMemoryInputSchema = z.object({
    ...mutationControl,
    expectedVersion: expectedVersionSchema,
    expectedStatus: retractableStatusSchema,
    reason: reasonSchema,
}).strict();
export const doNotReuseMemoryInputSchema = z.object({
    ...mutationControl,
    expectedVersion: expectedVersionSchema,
    expectedStatus: z.literal("active"),
    reason: reasonSchema,
}).strict();
export const restoreMemoryInputSchema = z.object({
    ...mutationControl,
    expectedVersion: expectedVersionSchema,
    expectedStatus: z.literal("retracted"),
    reason: reasonSchema,
}).strict();
export const forgetMemoryInputSchema = z.object({
    ...mutationControl,
    expectedVersion: expectedVersionSchema,
    expectedStatus: memoryStatusSchema,
}).strict();
export const editMemoryScopePreviewInputSchema = z.object({
    expectedVersion: expectedVersionSchema,
    expectedStatus: retractableStatusSchema,
    targetScopeType: memoryScopeTypeSchema,
    targetScopeId: identifierSchema,
}).strict();
export const editMemoryScopeInputSchema = editMemoryScopePreviewInputSchema.extend({
    ...mutationControl,
}).strict();
export const memoryFeedbackEventSchema = z.object({
    id: identifierSchema,
    tenantSlug: identifierSchema,
    organisationId: identifierSchema.optional(),
    actorId: identifierSchema,
    outcome: z.enum(["retracted", "do_not_reuse", "reuse_restored"]),
    targetKind: z.enum(["intentional_version", "canonical_episode"]),
    targetRef: identifierSchema,
    memoryId: identifierSchema.optional(),
    reversesEventId: identifierSchema.optional(),
    reason: reasonSchema,
    createdAt: timestampSchema,
}).strict().superRefine((event, context) => {
    if ((event.outcome === "reuse_restored") !== (event.reversesEventId !== undefined)) {
        context.addIssue({
            code: "custom",
            path: ["reversesEventId"],
            message: "reuse_restored must reference the do_not_reuse event it reverses",
        });
    }
    if (event.targetKind === "intentional_version" && event.memoryId === undefined) {
        context.addIssue({
            code: "custom",
            path: ["memoryId"],
            message: "intentional version feedback requires a memoryId",
        });
    }
    if (event.targetKind === "canonical_episode" && event.memoryId !== undefined) {
        context.addIssue({
            code: "custom",
            path: ["memoryId"],
            message: "canonical episode feedback cannot reference an intentional memoryId",
        });
    }
});
export const memoryHistoryResponseSchema = z.object({
    versions: z.array(memoryVersionSchema),
    relations: z.array(memoryRelationSchema),
    feedbackEvents: z.array(memoryFeedbackEventSchema),
}).strict();
export const memoryActionCapabilitiesResponseSchema = z.object({
    memory: memoryEntrySchema,
    actions: z.object({
        approve: z.boolean(),
        correct: z.boolean(),
        retract: z.boolean(),
        doNotReuse: z.boolean(),
        restore: z.boolean(),
        editScope: z.boolean(),
        forget: z.boolean(),
    }).strict(),
    confirmationRequired: z.literal(true),
    idempotencyRequired: z.literal(true),
}).strict();
export const editMemoryScopePreviewResponseSchema = z.object({
    source: memoryEntrySchema,
    targetScopeType: memoryScopeTypeSchema,
    targetScopeId: identifierSchema,
    consequences: z.array(z.string().trim().min(1)).min(2),
    confirmationRequired: z.literal(true),
}).strict();
const receiptEnvelope = { receipt: memoryLifecycleReceiptSchema };
export const createMemoryResponseSchema = z.object({
    ...receiptEnvelope,
    memory: memoryEntrySchema,
}).strict();
export const approveMemoryResponseSchema = createMemoryResponseSchema;
export const correctMemoryResponseSchema = z.object({
    ...receiptEnvelope,
    replacement: memoryVersionSchema,
    head: memoryEntrySchema,
    relation: memoryRelationSchema,
    replacedVersionId: memoryVersionIdSchema,
    transitionVersionId: memoryVersionIdSchema,
}).strict();
export const memoryFeedbackMutationResponseSchema = z.object({
    ...receiptEnvelope,
    memory: memoryVersionSchema,
    head: memoryEntrySchema,
    event: memoryFeedbackEventSchema,
}).strict();
export const retractMemoryResponseSchema = z.object({
    ...receiptEnvelope,
    memory: memoryVersionSchema,
    head: memoryEntrySchema,
    event: memoryFeedbackEventSchema,
}).strict();
export const editMemoryScopeResponseSchema = z.object({
    ...receiptEnvelope,
    source: memoryVersionSchema,
    replacement: memoryEntrySchema,
    relation: memoryRelationSchema,
}).strict();
export const memoryScopeCopyResponseSchema = z.object({
    source: memoryVersionSchema,
    replacement: memoryEntrySchema,
    relation: memoryRelationSchema,
}).strict();
export const forgetMemoryResponseSchema = z.object({
    ...receiptEnvelope,
    deletedMemoryId: identifierSchema,
    deletedBy: identifierSchema,
}).strict();
